/* ═══════════════════════════════════════════════════════════
   INSURANCE MEMBER / POLICY ID GUARD
   Keeps a Social Security Number out of the plaintext insurance
   policy-ID field (clients.insurance_id) while still allowing the
   payers who legitimately issue 9-digit numeric member IDs.

   The SSN itself is handled elsewhere: it stays in its own field
   and is routed to the encrypted vault path (client_ssn_vault /
   tg_redact_ssn). Nothing here touches that path.

   evaluateInsuranceId() is the single source of truth for the three
   consumer paths in App.jsx — inline validation, collectIntakeData,
   and export/PDF rendering — so they can never disagree about whether
   a given value is safe to store.
   ═══════════════════════════════════════════════════════════ */

// SSN-shaped = exactly 9 digits once separators are stripped
// (e.g. "123-45-6789" or "123456789").
export function isSSNShaped(v) {
  const digits = String(v || "").replace(/\D/g, "");
  return digits.length === 9;
}

/**
 * Classify a value typed into the insurance member/policy-ID field.
 *
 * @param {string}  insId             raw policy-ID field value
 * @param {boolean} confirmedMemberId patient checked "this is my member ID,
 *                                     not my SSN"
 * @returns {{
 *   status: "ok" | "needs_confirm",
 *   ssnShaped: boolean,   // value looks like an SSN → surface the confirm box
 *   needsConfirm: boolean,// SSN-shaped AND not yet confirmed
 *   blocked: boolean,     // must not proceed / must not be stored yet
 *   safeValue: string,    // value safe to persist & export ("" when blocked)
 * }}
 *
 * Rules:
 *  - Empty / non-SSN-shaped → "ok", stored as-is.
 *  - SSN-shaped and NOT confirmed → "needs_confirm": block the form, keep the
 *    value out of storage and export until the patient attests it isn't an SSN.
 *  - SSN-shaped and confirmed → "ok": the patient has attested it is genuinely
 *    their member/policy ID (some payers issue 9-digit numeric IDs), so it is
 *    allowed. The attestation is what makes storing it defensible.
 */
export function evaluateInsuranceId(insId, confirmedMemberId) {
  const raw = String(insId || "");
  const value = raw.trim();
  if (!value) {
    return { status: "ok", ssnShaped: false, needsConfirm: false, blocked: false, safeValue: "" };
  }
  const ssnShaped = isSSNShaped(value);
  if (ssnShaped && !confirmedMemberId) {
    return { status: "needs_confirm", ssnShaped: true, needsConfirm: true, blocked: true, safeValue: "" };
  }
  return { status: "ok", ssnShaped, needsConfirm: false, blocked: false, safeValue: value };
}
